import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { fetchButtonClickStatistics } from '../../api/userInteractionReports';

/**
 * Component for displaying button click statistics
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 */
function ButtonClicksReport({ filters }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Load button click statistics when filters change
  useEffect(() => {
    const loadStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchButtonClickStatistics(filters);
        setStatistics(data);
      } catch (error) {
        console.error('Error loading button click statistics:', error);
        setError('Failed to load button click statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [filters]);

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // If no data, show message
  if (!statistics || statistics.totalButtonClicks === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No button click data available for the selected filters.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Button Click Statistics
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Total Button Clicks Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Button Clicks
            </Typography>
            <Typography variant="h4">
              {statistics.totalButtonClicks}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Clicked Button Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Clicked Button
            </Typography>
            <Typography variant="h5">
              {statistics.mostClickedButtons?.[0]?.buttonId || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {statistics.mostClickedButtons?.[0]?.count || 0} clicks
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Active Component Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Active Component
            </Typography>
            <Typography variant="h5">
              {Object.entries(statistics.buttonClicksByComponent || {})
                .sort((a, b) => b[1] - a[1])
                .map(([component]) => component)[0] || 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Button Clicks by Day Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Button Clicks by Day
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={statistics.buttonClicksByDayArray || []}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" name="Button Clicks" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      
      {/* Most Clicked Buttons Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Most Clicked Buttons
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="most clicked buttons table">
            <TableHead>
              <TableRow>
                <TableCell>Button</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(statistics.mostClickedButtons || []).map((button) => (
                <TableRow
                  key={button.buttonId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {button.buttonId}
                  </TableCell>
                  <TableCell align="right">{button.count}</TableCell>
                  <TableCell align="right">
                    {((button.count / statistics.totalButtonClicks) * 100).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Button Clicks by Component Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Button Clicks by Component
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="button clicks by component table">
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(statistics.buttonClicksByComponent || {})
                .sort((a, b) => b[1] - a[1])
                .map(([component, count]) => (
                  <TableRow
                    key={component}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {component}
                    </TableCell>
                    <TableCell align="right">{count}</TableCell>
                    <TableCell align="right">
                      {((count / statistics.totalButtonClicks) * 100).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default ButtonClicksReport;
